import type {MatchEvent} from '@/components/wyscout/MatchEvents/types';
import type {WyscoutFixtureSubstitution} from '@/features/players/wyscoutFixtureApi';

export type PlayerMatchRelationship =
  | 'actor'
  | 'pass_recipient'
  | 'duel_opponent'
  | 'foul_opponent'
  | 'goalkeeper'
  | 'related_event';

export interface PlayerMatchMoment {
  event: MatchEvent;
  relationship: PlayerMatchRelationship;
  relationshipLabel: string;
}

export type PlayerMatchMomentItem =
  | ({kind: 'event'} & PlayerMatchMoment)
  | {kind: 'substitution'; minute: number; direction: 'on' | 'off'; counterpartPlayerId: number; teamId: number};

const relationship = (event: MatchEvent, playerId: number): Omit<PlayerMatchMoment, 'event'> | null => {
  if (event.player?.id === playerId) return {relationship: 'actor', relationshipLabel: 'Player action'};
  if (event.pass?.recipient?.id === playerId) return {relationship: 'pass_recipient', relationshipLabel: 'Pass recipient'};
  if (event.groundDuel?.opponent?.id === playerId || event.aerialDuel?.opponent?.id === playerId) {
    return {relationship: 'duel_opponent', relationshipLabel: 'Duel opponent'};
  }
  if (event.infraction?.opponent?.id === playerId) return {relationship: 'foul_opponent', relationshipLabel: 'Foul opponent'};
  if (event.shot?.goalkeeper?.id === playerId) return {relationship: 'goalkeeper', relationshipLabel: 'Goalkeeper'};
  return null;
};

export const playerMatchMoments = (events: readonly MatchEvent[], wyscoutPlayerId: number): PlayerMatchMoment[] => {
  const targetActorEventIds = new Set(events.filter(event => event.player?.id === wyscoutPlayerId).map(event => event.id));
  const linkedEventIds = new Set<number>();
  for (const event of events) {
    if (event.player?.id === wyscoutPlayerId && event.relatedEventId) linkedEventIds.add(event.relatedEventId);
    if (event.relatedEventId && targetActorEventIds.has(event.relatedEventId)) linkedEventIds.add(event.id);
  }

  return events
    .map((event, index) => ({
      event,
      index,
      relationship: relationship(event, wyscoutPlayerId)
        ?? (linkedEventIds.has(event.id) ? {relationship: 'related_event' as const, relationshipLabel: 'Related event'} : null),
    }))
    .filter((candidate): candidate is typeof candidate & {relationship: Omit<PlayerMatchMoment, 'event'>} =>
      candidate.relationship !== null)
    .sort((left, right) => left.event.minute - right.event.minute
      || left.event.second - right.event.second
      || left.index - right.index)
    .map(({event, relationship}) => ({event, ...relationship}));
};

export const playerMatchMomentItems = (
  events: readonly MatchEvent[],
  substitutions: readonly WyscoutFixtureSubstitution[],
  wyscoutPlayerId: number,
): PlayerMatchMomentItem[] => {
  const eventItems: PlayerMatchMomentItem[] = playerMatchMoments(events, wyscoutPlayerId)
    .map(moment => ({kind: 'event', ...moment}));
  const substitutionItems = substitutions.flatMap((substitution): PlayerMatchMomentItem[] => {
    if (substitution.playerInId === wyscoutPlayerId) {
      return [{kind: 'substitution' as const, minute: substitution.minute, direction: 'on' as const,
        counterpartPlayerId: substitution.playerOutId, teamId: substitution.teamId}];
    }
    if (substitution.playerOutId === wyscoutPlayerId) {
      return [{kind: 'substitution' as const, minute: substitution.minute, direction: 'off' as const,
        counterpartPlayerId: substitution.playerInId, teamId: substitution.teamId}];
    }
    return [];
  });
  return [...eventItems, ...substitutionItems].sort((left, right) => {
    const leftMinute = left.kind === 'event' ? left.event.minute : left.minute;
    const rightMinute = right.kind === 'event' ? right.event.minute : right.minute;
    if (leftMinute !== rightMinute) return leftMinute - rightMinute;
    if (left.kind === 'event' && right.kind === 'event') return left.event.second - right.event.second;
    return left.kind === 'event' ? -1 : right.kind === 'event' ? 1 : 0;
  });
};
